import {
  forwardRef,
  type ReactNode,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import {
  PtyTerminal,
  type PtyTerminalHandle,
  type PtyTerminalProps,
  type PtyTerminalStatus,
} from "./PtyTerminal";
import { pluginSdkT, usePluginSdkT } from "../i18n";

function RotateIcon({ size = 13 }: { size?: number }) {
  return (
    <svg
      aria-hidden="true"
      fill="none"
      height={size}
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
    >
      <path d="M21 12a9 9 0 1 1-2.64-6.36" />
      <path d="M21 3v6h-6" />
    </svg>
  );
}

function CloseIcon({ size = 13 }: { size?: number }) {
  return (
    <svg
      aria-hidden="true"
      fill="none"
      height={size}
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
    >
      <circle cx="12" cy="12" r="10" />
      <path d="m15 9-6 6" />
      <path d="m9 9 6 6" />
    </svg>
  );
}

function PanelIconButton({
  title,
  danger,
  onClick,
  children,
}: {
  title: string;
  danger?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  const [showTip, setShowTip] = useState(false);

  return (
    <button
      aria-label={title}
      className={`relative rounded p-1 transition-colors ${
        danger
          ? "text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
          : "text-muted-foreground hover:bg-muted/60 hover:text-foreground"
      }`}
      onClick={onClick}
      onBlur={() => setShowTip(false)}
      onFocus={() => setShowTip(true)}
      onMouseEnter={() => setShowTip(true)}
      onMouseLeave={() => setShowTip(false)}
      type="button"
    >
      {children}
      {showTip ? (
        <span className="pointer-events-none absolute right-0 top-full z-50 mt-1 whitespace-nowrap rounded bg-foreground px-1.5 py-0.5 text-[10px] text-background shadow-sm">
          {title}
        </span>
      ) : null}
    </button>
  );
}

export function formatPtyTerminalStatus(status: PtyTerminalStatus): string {
  switch (status) {
    case "active":
      return pluginSdkT("pty.status.active");
    case "connecting":
      return pluginSdkT("pty.status.connecting");
    case "ended":
      return pluginSdkT("pty.status.ended");
    case "error":
      return pluginSdkT("pty.status.error");
  }
}

function statusClassName(status: PtyTerminalStatus): string {
  switch (status) {
    case "active":
      return "plugin-tone-success-text";
    case "connecting":
      return "plugin-tone-warning-text";
    case "error":
      return "plugin-tone-error-text";
    case "ended":
      return "text-muted-foreground";
  }
}

export interface PtyTerminalPanelProps extends Omit<
  PtyTerminalProps,
  "onStatusChange" | "onEof"
> {
  title?: ReactNode;
  statusPrefix?: ReactNode;
  actions?: ReactNode;
  onRestart?: () => void | Promise<void>;
  onClose?: () => void | Promise<void>;
  onClosed?: () => void;
  onStatusChange?: (status: PtyTerminalStatus) => void;
  onEof?: () => void;
  closeMessage?: string;
  frameless?: boolean;
  className?: string;
  headerClassName?: string;
  bodyClassName?: string;
}

export const PtyTerminalPanel = forwardRef<
  PtyTerminalHandle,
  PtyTerminalPanelProps
>(function PtyTerminalPanel(
  {
    title,
    statusPrefix,
    actions,
    onRestart,
    onClose,
    onClosed,
    onStatusChange,
    onEof,
    closeMessage,
    frameless = false,
    className,
    headerClassName,
    bodyClassName,
    ...terminalProps
  },
  ref,
) {
  const t = usePluginSdkT();
  const resolvedTitle = title ?? t("pty.title");
  const resolvedCloseMessage = closeMessage ?? t("pty.closedMessage");
  const terminalRef = useRef<PtyTerminalHandle>(null);
  const [status, setStatus] = useState<PtyTerminalStatus>("connecting");
  const { callSidecar, fillAvailable, sessionId } = terminalProps;

  useImperativeHandle(
    ref,
    () => ({
      fit: () => terminalRef.current?.fit(),
      focus: () => terminalRef.current?.focus(),
      refresh: () => terminalRef.current?.refresh(),
      write: (data: string) => terminalRef.current?.write(data),
    }),
    [],
  );

  const updateStatus = useCallback(
    (next: PtyTerminalStatus) => {
      setStatus(next);
      onStatusChange?.(next);
    },
    [onStatusChange],
  );

  const closeCurrentSession = useCallback(async () => {
    if (callSidecar) {
      await callSidecar("terminal", "terminal/pty-close", { sessionId }).catch(
        () => {},
      );
    }
    updateStatus("ended");
    terminalRef.current?.write(`\r\n\x1b[2m${resolvedCloseMessage}\x1b[0m\r\n`);
  }, [callSidecar, resolvedCloseMessage, sessionId, updateStatus]);

  const handleClose = useCallback(async () => {
    await closeCurrentSession();
    await onClose?.();
    onClosed?.();
  }, [closeCurrentSession, onClose, onClosed]);

  const handleRestart = useCallback(async () => {
    await closeCurrentSession();
    await onRestart?.();
  }, [closeCurrentSession, onRestart]);

  const handleEof = useCallback(() => {
    updateStatus("ended");
    onEof?.();
    onClosed?.();
  }, [onClosed, onEof, updateStatus]);

  return (
    <div
      className={`${fillAvailable ? "flex h-full min-h-0 flex-col" : ""} bg-background ${
        frameless
          ? "overflow-visible"
          : "overflow-hidden rounded-md border border-border"
      } ${className ?? ""}`}
    >
      <div
        className={`flex shrink-0 items-center gap-2 border-b border-border bg-muted/10 px-3 py-2 ${headerClassName ?? ""}`}
      >
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-center gap-2">
            <span className="truncate text-[11px] font-medium text-foreground">
              {resolvedTitle}
            </span>
            <span
              className={`shrink-0 text-[10px] font-mono ${statusClassName(status)}`}
            >
              {statusPrefix ? <>{statusPrefix} </> : null}
              {formatPtyTerminalStatus(status)}
            </span>
          </div>
        </div>
        {actions ? (
          <div className="flex items-center gap-1">{actions}</div>
        ) : null}
        <div className="flex items-center gap-1">
          {onRestart ? (
            <PanelIconButton
              onClick={() => void handleRestart()}
              title={t("pty.restart")}
            >
              <RotateIcon />
            </PanelIconButton>
          ) : null}
          <PanelIconButton
            danger
            onClick={() => void handleClose()}
            title={t("pty.close")}
          >
            <CloseIcon />
          </PanelIconButton>
        </div>
      </div>
      <div className={`${fillAvailable ? "min-h-0 flex-1" : ""} ${bodyClassName ?? ""}`.trim()}>
        <PtyTerminal
          {...terminalProps}
          ref={terminalRef}
          onEof={handleEof}
          onStatusChange={updateStatus}
        />
      </div>
    </div>
  );
});
